import { useState, useRef, useEffect, useCallback } from 'react'
import { jsPDF } from 'jspdf'
import './AdoptionFormPage.css'

// ── Icônes ─────────────────────────────────────────────────────────────────
const IcoCheck    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcoDownload = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
const IcoTrash    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
const IcoChevronLeft = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
const IcoPen      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
const IcoSend     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>

const SECTION_LABELS = ['Informations personnelles','Logement','Expérience & attentes','Animaux actuels','Engagements','Informations additionnelles','Signature']
const TOTAL_STEPS    = SECTION_LABELS.length

// ── Helpers PDF ─────────────────────────────────────────────────────────────
export function generateAdoptionPDF({ data, signatureDataUrl, user, formNumber, logoDataUrl }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const W = 210
  const M = 14
  const NAVY = [18, 34, 74]
  const GOLD = [217, 174, 92]
  const GRAY = [156, 163, 175]
  const BODY = [55, 65, 81]
  const WHITE = [255, 255, 255]
  const { s1, s2, s3, s4, s5, s6 } = data

  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
  const adoptantName = (user && user.name) ? user.name : ((s1 && s1.nomPrenom) ? s1.nomPrenom : '—')

  const fc = (...c) => doc.setFillColor(...c)
  const dc = (...c) => doc.setDrawColor(...c)
  const tc = (...c) => doc.setTextColor(...c)
  const lw = (w) => doc.setLineWidth(w)

  // ── Fonction pour afficher un champ avec label et valeur sur une ligne ──
  const field = (label, value, x, y, maxWidth, bold = false) => {
    const labelWidth = 60 // Largeur fixe pour le label
    tc(...GOLD)
    doc.setFontSize(8.5)
    doc.setFont('helvetica', 'bold')
    doc.text(label + ' :', x, y)
    
    tc(...NAVY)
    doc.setFontSize(8.5)
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    const text = String(value || '—')
    const maxW = maxWidth - labelWidth - 4
    const lines = doc.splitTextToSize(text, maxW)
    doc.text(lines, x + labelWidth, y)
    return y + 7.5 // Espacement entre les lignes
  }

  // ── En-tête de section avec plus d'espace ──
  const sec = (num, title, y) => {
       
    fc(...NAVY)
    doc.rect(M, y, W - M * 2, 10, 'F')
    fc(...GOLD)
    doc.rect(M, y, 4, 10, 'F')
    tc(...WHITE)
    doc.setFontSize(9.5)
    doc.setFont('helvetica', 'bold')
    doc.text(num + '. ' + title, M + 10, y + 7)
    return y + 18 // Plus d'espace après le titre
  }

  // ── Pied de page ──
  const foot = () => {
    const footY = 278
    fc(...GOLD)
    doc.rect(0, footY, W, 1, 'F')
    fc(...NAVY)
    doc.rect(0, footY + 1, W, 18, 'F')
    tc(...WHITE)
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'bold')
    doc.text('Animaux pour Adoption de la France  |  animauxdecompagnie@animauxpouradoptiondelafrance.com  |  0757750092', W/2, footY + 8, { align: 'center' })
    tc(160, 175, 200)
    doc.setFontSize(6)
    doc.setFont('helvetica', 'normal')
    doc.text('Conservez ce justificatif.', W/2, footY + 14, { align: 'center' })
  }

  // ═══════════════════════════════════════════════════
  // PAGE 1
  // ═══════════════════════════════════════════════════

  // ── HEADER ──
  const hH = 48
  fc(...NAVY)
  doc.rect(0, 0, W, hH, 'F')
  fc(...GOLD)
  doc.rect(0, 0, 4, hH, 'F')
  
  // Logo
  fc(...WHITE)
  doc.roundedRect(M, 6, 30, 30, 4, 4, 'F')
  if (logoDataUrl) {
    try { doc.addImage(logoDataUrl, 'JPEG', M + 1, 7, 28, 28) } catch(e) {}
  }
  
  // Titre
  tc(...WHITE)
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.text('Animaux pour Adoption', M + 36, 18)
  
  tc(...GOLD)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text('de la France', M + 36, 27)
  
  tc(160, 175, 200)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'normal')
  doc.text('animauxdecompagnie@animauxpouradoptiondelafrance.com', M + 36, 36)
  
  // Badge "ADOPTION" à droite
  tc(...GOLD)
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text("ADOPTION", W - M, 20, { align: 'right' })
  
  tc(160, 175, 200)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'normal')
  doc.text(formNumber || '', W - M, 30, { align: 'right' })
  doc.text(dateStr, W - M, 38, { align: 'right' })

  let y = hH + 8

  // ── SECTION 1 : Informations personnelles ──
  y = sec(1, "Informations personnelles de l'adoptant", y)
  y = field('Nom et prénom', s1?.nomPrenom, M, y, W - M * 2, true)
  y = field('Date de naissance', s1?.dateNaissance, M, y, W - M * 2)
  y = field('Adresse complète', s1?.adresse, M, y, W - M * 2)
  y = field('Téléphone', s1?.telephone, M, y, W - M * 2)
  y = field('Email', s1?.email, M, y, W - M * 2)
  y = field('Profession', s1?.profession, M, y, W - M * 2)
  y = field('Situation familiale', s1?.situationFamil, M, y, W - M * 2)
  y = field('Adultes au foyer', s1?.nbAdultes, M, y, W - M * 2)
  y = field('Enfants et âges', s1?.nbEnfants, M, y, W - M * 2)
  y += 3 // Espace après la section

  // ── SECTION 2 : Logement ──
  y = sec(2, 'Logement et environnement', y)
  y = field('Type de logement', s2?.typeLogement, M, y, W - M * 2)
  y = field('Statut occupation', s2?.proprietaire, M, y, W - M * 2)
  y = field('Superficie', s2?.superficie, M, y, W - M * 2)
  y = field('Jardin / Balcon', s2?.jardinBalcon, M, y, W - M * 2)
  y = field('Accès extérieur', s2?.accesExterieur, M, y, W - M * 2)
  y = field('Temps seul / jour', s2?.tempsSeul, M, y, W - M * 2)
  y = field('Lieu de couchage', s2?.lieuDodo, M, y, W - M * 2)
  y += 3

  // ── SECTION 3 : Animaux actuels ──
  y = sec(3, 'Animaux actuels au foyer', y)
  y = field('Animaux actuels', s4?.animauxActuels, M, y, W - M * 2)
  y = field("Réaction à d'autres animaux", s4?.reactionAnimaux, M, y, W - M * 2)
  y = field('Vaccinés et identifiés', s4?.vaccinesId, M, y, W - M * 2)
  y += 3

  // ── SECTION 4 : Expérience et attentes ──
  // Vérifier si on a assez de place sur la page 1
  if (y + 30 > 270) {
    foot()
    doc.addPage()
    
    // Mini header page 2
    fc(...NAVY)
    doc.rect(0, 0, W, 14, 'F')
    fc(...GOLD)
    doc.rect(0, 0, 4, 14, 'F')
    tc(...WHITE)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text('Animaux pour Adoption de la France', M + 4, 9)
    tc(175, 188, 210)
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'normal')
    doc.text(formNumber + ' — ' + adoptantName, W - M, 9, { align: 'right' })
    
    y = 18
  }
  
  y = sec(4, "Expérience et attentes", y)
  y = field('Animaux de compagnie passés', s3?.animalsPasses, M, y, W - M * 2)
  y = field("Raison de l'adoption", s3?.raisonAdoption, M, y, W - M * 2)
  y = field("Type d'animal souhaité", s3?.typeAnimal, M, y, W - M * 2)
  y = field('Préférences', s3?.preferences, M, y, W - M * 2)
  y = field('Conscient des coûts', s3?.conscientCouts, M, y, W - M * 2)
  y = field('Absence prolongée', s3?.absenceProlongee, M, y, W - M * 2)
  y = field('Problèmes de comportement', s3?.problComportement, M, y, W - M * 2)
  y += 3

  // ── SECTION 5 : Engagements ──
  if (y + 30 > 270) {
    foot()
    doc.addPage()
  
    // Mini header
    fc(...NAVY)
    doc.rect(0, 0, W, 14, 'F')
    fc(...GOLD)
    doc.rect(0, 0, 4, 14, 'F')
    tc(...WHITE)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text('Animaux pour Adoption de la France', M + 4, 9)
    tc(175, 188, 210)
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'normal')
    doc.text(formNumber + ' — ' + adoptantName, W - M, 9, { align: 'right' })
    
    y = 18
  }

  y = sec(5, 'Engagements et compréhension', y)

  const engs = [
    ["Je comprends que l'adoption est un engagement sur le long terme (10 à 15 ans ou plus)", s5?.engagementLongTerme],
    ["J'accepte les visites de pré-adoption et de post-adoption de l'association", s5?.accepteVisites],
    ["Je suis prêt(e) à offrir à l'animal les soins vétérinaires nécessaires tout au long de sa vie", s5?.soinVeteriaire],
    ["J'accepte de signer un contrat d'adoption avec l'association", s5?.accepteContrat],
  ]
  
  engs.forEach(([label, checked]) => {
    // Checkbox
    fc(...WHITE)
    dc(150, 150, 150)
    lw(0.3)
    doc.rect(M, y + 1, 4, 4, 'FD')
    if (checked) {
      dc(...NAVY)
      lw(0.5)
      doc.line(M + 0.6, y + 1 + 4 * 0.55, M + 4 * 0.4, y + 1 + 4 - 0.6)
      doc.line(M + 4 * 0.4, y + 1 + 4 - 0.6, M + 4 - 0.6, y + 1 + 0.6)
    }
    tc(...NAVY)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    const lines = doc.splitTextToSize(label, W - M * 2 - 14)
    doc.text(lines, M + 8, y + 4.5)
    y += 7 * lines.length + 4
  })
  y += 3

  // ── SECTION 6 : Informations additionnelles ──
  if (s6?.autresInfos && s6.autresInfos.trim()) {
    y = sec(6, 'Informations additionnelles', y)
    y = field('Commentaires', s6.autresInfos, M, y, W - M * 2)
    y += 8
  } else {
    y += 3
  }

  // ── SIGNATURE ──
  if (y + 30 > 270) {
    foot()
    doc.addPage()
    
    // Mini header
    fc(...NAVY)
    doc.rect(0, 0, W, 14, 'F')
    fc(...GOLD)
    doc.rect(0, 0, 4, 14, 'F')
    tc(...WHITE)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text('Animaux pour Adoption de la France', M + 4, 9)
    tc(175, 188, 210)
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'normal')
    doc.text(formNumber + ' — ' + adoptantName, W - M, 9, { align: 'right' })
    
    y = 18
  }

  // Titre signature
  fc(...NAVY)
  doc.rect(M, y, W - M * 2, 10, 'F')
  fc(...GOLD)
  doc.rect(M, y, 4, 10, 'F')
  tc(...WHITE)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text('SIGNATURE NUMÉRIQUE DE L\'ADOPTANT', M + 10, y + 7)
  y += 13

  const sigW = W - M * 2
  const sigH = 40
  
  // Cadre de signature avec bordures en pointillés
  dc(...GOLD)
  lw(0.55)
  for (let x = M; x < M + sigW; x += 5) {
    doc.line(x, y, Math.min(x + 2.8, M + sigW), y)
    doc.line(x, y + sigH, Math.min(x + 2.8, M + sigW), y + sigH)
  }
  for (let yy = y; yy < y + sigH; yy += 5) {
    doc.line(M, yy, M, Math.min(yy + 2.8, y + sigH))
    doc.line(M + sigW, yy, M + sigW, Math.min(yy + 2.8, y + sigH))
  }

  // Zone signature à gauche
  const siW = 112
  fc(250, 250, 250)
  doc.rect(M + 1.5, y + 1.5, siW, sigH - 3, 'F')
  if (signatureDataUrl) {
    try {
      doc.addImage(signatureDataUrl, 'PNG', M + 2, y + 2, siW - 1, sigH - 4)
    } catch(e) {}
  }
  
  // Ligne sous signature
  dc(...NAVY)
  lw(0.2)
  doc.setGState(doc.GState({ opacity: 0.18 }))
  doc.line(M + 4, y + sigH - 5, M + siW - 2, y + sigH - 5)
  doc.setGState(doc.GState({ opacity: 1 }))
  tc(...GRAY)
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  
  // Infos à droite
  const rx = M + siW + 12
  const infoData = [
    ['Nom complet', adoptantName],
    ['Date de signature', dateStr],
    ['Référence', formNumber],
  ]
  
  infoData.forEach(([lbl, val], i) => {
    const ry = y + 5 + i * 10.5
    tc(...GOLD)
    doc.setFontSize(6)
    doc.setFont('helvetica', 'bold')
    doc.text(lbl.toUpperCase(), rx, ry)
    tc(...NAVY)
    doc.setFontSize(i === 3 ? 7.5 : 8.5)
    doc.setFont('helvetica', 'bold')
    doc.text(val, rx, ry + 5.5)
  })

  y += sigH + 10
  
  // ── NOTE ──
  fc(252, 250, 245)
  doc.rect(M, y, W - M * 2, 18, 'F')
  fc(...GOLD)
  doc.rect(M, y, 3, 18, 'F')
  tc(...GOLD)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.text('i', M + 7, y + 11)
  tc(...BODY)
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  const noteLines = doc.splitTextToSize(
    "Ce formulaire a été rempli et signé électroniquement via la plateforme Animaux pour Adoption de la France. La signature numérique apposée atteste du consentement de l'adoptant à l'ensemble des engagements listés ci-dessus.",
    W - M * 2 - 16
  )
  doc.text(noteLines, M + 14, y + 7)

  foot()
  return doc
}

// ════════════════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════════════════
export default function AdoptionFormPage({ user, onBack, adoptionAPI }) {
  const [step, setStep]           = useState(1)
  const [saving, setSaving]       = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError]         = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [pdfBlob, setPdfBlob]     = useState(null)
  const [savedFormNumber, setSavedFormNumber] = useState('')

  // ── Champs du formulaire ──────────────────────────
  const [s1, setS1] = useState({ nomPrenom: user?.name || '', dateNaissance: '', adresse: '', telephone: user?.phone || '', email: user?.email || '', profession: '', situationFamil: '', nbAdultes: '', nbEnfants: '' })
  const [s2, setS2] = useState({ typeLogement: '', proprietaire: '', superficie: '', jardinBalcon: '', accesExterieur: '', tempsSeul: '', lieuDodo: '' })
  const [s3, setS3] = useState({ animalsPasses: '', raisonAdoption: '', typeAnimal: '', preferences: '', conscientCouts: '', problComportement: '', absenceProlongee: '' })
  const [s4, setS4] = useState({ animauxActuels: '', reactionAnimaux: '', vaccinesId: '' })
  const [s5, setS5] = useState({ engagementLongTerme: false, accepteVisites: false, soinVeteriaire: false, accepteContrat: false })
  const [s6, setS6] = useState({ autresInfos: '' })

  // ── Canvas signature ──────────────────────────────
  const canvasRef    = useRef(null)
  const drawing      = useRef(false)
  const [hasSig, setHasSig] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.lineWidth   = 2.5
    ctx.lineCap     = 'round'
    ctx.strokeStyle = '#12224A'
    ctx.fillStyle   = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }, [step])

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }

  const startDraw = useCallback((e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    drawing.current = true
    const pos = getPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }, [])

  const draw = useCallback((e) => {
    e.preventDefault()
    if (!drawing.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const pos = getPos(e, canvas)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    setHasSig(true)
  }, [])

  const stopDraw = useCallback(() => { drawing.current = false }, [])

  const clearSig = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setHasSig(false)
  }

  const field = (label, key, state, setState, opts = {}) => {
    const isEmpty = showErrors && !String(state[key] || '').trim()
    const cls = `af-input${opts.textarea ? ' af-textarea' : ''}${isEmpty ? ' af-invalid' : ''}`
    return (
      <div className="af-fg" key={key}>
        <label className="af-label">
          {label}
          {opts.required && <span className="af-required">*</span>}
        </label>
        {opts.textarea
          ? <textarea className={cls} value={state[key]} onChange={e => { setState(s => ({ ...s, [key]: e.target.value })); if (showErrors) setShowErrors(false) }} placeholder={opts.placeholder || ''} rows={3} required={opts.required} />
          : <input className={cls} type={opts.type || 'text'} value={state[key]} onChange={e => { setState(s => ({ ...s, [key]: e.target.value })); if (showErrors) setShowErrors(false) }} placeholder={opts.placeholder || ''} required={opts.required} />
        }
      </div>
    )
  }

  const check = (label, key) => (
    <label className="af-check" key={key}>
      <input type="checkbox" checked={s5[key]} onChange={e => setS5(s => ({ ...s, [key]: e.target.checked }))} />
      <span>{label}</span>
    </label>
  )

  // ── Validation par étape ─────────────────────────
  const isStepValid = (s) => {
    switch (s) {
      case 1:
        return !!(
          s1.nomPrenom?.trim() &&
          s1.dateNaissance?.trim() &&
          s1.adresse?.trim() &&
          s1.telephone?.trim() &&
          s1.email?.trim() &&
          s1.profession?.trim() &&
          s1.situationFamil?.trim() &&
          s1.nbAdultes?.toString().trim() &&
          s1.nbEnfants?.trim()
        )
      case 2:
        return !!(
          s2.typeLogement?.trim() &&
          s2.proprietaire?.trim() &&
          s2.superficie?.trim() &&
          s2.jardinBalcon?.trim() &&
          s2.accesExterieur?.trim() &&
          s2.tempsSeul?.trim() &&
          s2.lieuDodo?.trim()
        )
      case 3:
        return !!(
          s3.animalsPasses?.trim() &&
          s3.raisonAdoption?.trim() &&
          s3.typeAnimal?.trim() &&
          s3.preferences?.trim() &&
          s3.conscientCouts?.trim() &&
          s3.problComportement?.trim() &&
          s3.absenceProlongee?.trim()
        )
      case 4:
        return !!(
          s4.animauxActuels?.trim() &&
          s4.reactionAnimaux?.trim() &&
          s4.vaccinesId?.trim()
        )
      case 5:
        return !!(
          s5.engagementLongTerme &&
          s5.accepteVisites &&
          s5.soinVeteriaire &&
          s5.accepteContrat
        )
      case 6:
        return true
      case 7:
        return hasSig
      default:
        return true
    }
  }

  const stepValid = isStepValid(step)

  const stepHint = () => {
    switch (step) {
      case 1: return 'Remplissez tous les champs personnels pour continuer.'
      case 2: return 'Remplissez toutes les informations sur votre logement.'
      case 3: return 'Répondez à toutes les questions sur votre expérience.'
      case 4: return 'Décrivez les animaux actuels au foyer.'
      case 5: return 'Cochez toutes les cases pour confirmer vos engagements.'
      case 7: return 'Apposez votre signature pour valider le formulaire.'
      default: return ''
    }
  }

  // ── Soumission finale ─────────────────────────────
  const handleSubmit = async () => {
    const canvas = canvasRef.current
    if (!canvas || !hasSig) { setError('Veuillez apposer votre signature avant de continuer.'); return }
    if (!s1.nomPrenom) { setError('Le nom et prénom est obligatoire (section 1).'); return }
    if (!s5.accepteContrat) { setError('Vous devez accepter le contrat d\'adoption (section 5).'); return }

    const signatureDataUrl = canvas.toDataURL('image/png')
    setError('')
    setSaving(true)

    try {
      let logoDataUrl = null
      try {
        const logoResp = await fetch(require('../../assets/monlogo.jpeg'))
        const blob = await logoResp.blob()
        logoDataUrl = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob) })
      } catch {}

      const formData = { s1, s2, s3, s4, s5, s6 }
      const tempNum  = `ADOPT-${new Date().getFullYear()}-XXXXXX`
      const doc = generateAdoptionPDF({ data: formData, signatureDataUrl, user, formNumber: tempNum, logoDataUrl })
      const pdfBytes = doc.output('arraybuffer')
      const pdfB64   = doc.output('datauristring')

      const result = await adoptionAPI.submit({
        s1, s2, s3, s4, s5, s6,
        signatureDataUrl,
        pdfUrl: pdfB64.slice(0, 200),
      })

      setSavedFormNumber(result.form.formNumber)
      setPdfBlob(new Blob([pdfBytes], { type: 'application/pdf' }))
      setSubmitted(true)
    } catch (err) {
      setError(err.message || 'Erreur lors de la soumission. Réessayez.')
    } finally {
      setSaving(false)
    }
  }

  const downloadPDF = () => {
    if (!pdfBlob) return
    const url = URL.createObjectURL(pdfBlob)
    const a   = document.createElement('a')
    a.href    = url
    a.download = `${savedFormNumber || 'formulaire-adoption'}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Écran de succès ───────────────────────────────
  if (submitted) {
    return (
      <div className="af-success">
        <div className="af-success-icon"><IcoCheck /></div>
        <div className="af-success-title">Formulaire soumis avec succès !</div>
        <div className="af-success-num">{savedFormNumber}</div>
        <p className="af-success-sub">Notre équipe examinera votre demande et vous contactera prochainement. Vous pouvez télécharger votre exemplaire signé ci-dessous.</p>
        <button className="af-btn-primary" onClick={downloadPDF}><IcoDownload /> Télécharger mon formulaire (PDF)</button>
        <button className="af-btn-ghost" onClick={onBack} style={{ marginTop:10 }}>← Retour à mes factures</button>
      </div>
    )
  }

  const progress = Math.round((step / TOTAL_STEPS) * 100)

  return (
    <div className="af-wrap">
      {/* En-tête */}
      <div className="af-header">
        <button className="af-back" onClick={onBack} title="Retour"><IcoChevronLeft /></button>
        <div>
          <div className="af-header-title">Formulaire d'adoption</div>
          <div className="af-header-sub">Association Animaux pour Adoption de la France</div>
        </div>
        <div className="af-step-counter">{step} / {TOTAL_STEPS}</div>
      </div>

      {/* Barre de progression */}
      <div className="af-progress-track"><div className="af-progress-bar" style={{ width:`${progress}%` }} /></div>

      {/* Étapes */}
      <div className="af-steps-row">
        {SECTION_LABELS.map((label, i) => (
          <div key={i} className={`af-step-dot${step === i+1 ? ' active' : step > i+1 ? ' done' : ''}`} onClick={() => { if (i+1 < step) { setShowErrors(false); setStep(i+1) } }} title={label}>
            {step > i+1 ? <IcoCheck /> : i+1}
          </div>
        ))}
      </div>
      <div className="af-step-label">{SECTION_LABELS[step - 1]}</div>

      {/* Corps du formulaire */}
      <div className="af-body">
        {error && <div className="af-error">{error}</div>}

        {/* ── Section 1 ── */}
        {step === 1 && (
          <div className="af-section">
            <div className="af-section-title">Section 1 — Informations personnelles de l'adoptant</div>
            <div className="af-grid">
              {field('Nom et Prénom', 'nomPrenom', s1, setS1, { required: true })}
              {field('Date de naissance', 'dateNaissance', s1, setS1, { type:'date', required: true })}
              {field('Adresse complète', 'adresse', s1, setS1, { placeholder:'12 rue des Lilas, 75001 Paris', required: true })}
              {field('Téléphone', 'telephone', s1, setS1, { type:'tel', required: true })}
              {field('Email', 'email', s1, setS1, { type:'email', required: true })}
              {field('Profession', 'profession', s1, setS1, { required: true })}
              {field('Situation familiale', 'situationFamil', s1, setS1, { placeholder:'Célibataire, En couple, Marié(e)…', required: true })}
              {field('Nombre d\'adultes au foyer', 'nbAdultes', s1, setS1, { type:'number', min:0, required: true })}
              {field('Nombre d\'enfants et leurs âges', 'nbEnfants', s1, setS1, { placeholder:'2 enfants : 5 et 8 ans', required: true })}
            </div>
          </div>
        )}

        {/* ── Section 2 ── */}
        {step === 2 && (
          <div className="af-section">
            <div className="af-section-title">Section 2 — Logement et environnement</div>
            <div className="af-grid">
              {field('Type de logement', 'typeLogement', s2, setS2, { placeholder:'Appartement / Maison', required: true })}
              {field('Propriétaire ou locataire', 'proprietaire', s2, setS2, { placeholder:'Propriétaire / Locataire avec autorisation', required: true })}
              {field('Superficie approximative', 'superficie', s2, setS2, { placeholder:'75 m²', required: true })}
              {field('Jardin / Balcon (clôturé ?)', 'jardinBalcon', s2, setS2, { textarea:true, placeholder:'Balcon de 8 m² sécurisé avec filets', required: true })}
              {field('Accès extérieur et sécurisation', 'accesExterieur', s2, setS2, { textarea:true, required: true })}
              {field('Temps seul par jour', 'tempsSeul', s2, setS2, { placeholder:'3 à 4 heures', required: true })}
              {field('Lieu de couchage de l\'animal', 'lieuDodo', s2, setS2, { placeholder:'Panier dans la chambre', required: true })}
            </div>
          </div>
        )}

        {/* ── Section 3 ── */}
        {step === 3 && (
          <div className="af-section">
            <div className="af-section-title">Section 3 — Expérience et attentes</div>
            <div className="af-grid af-grid-1">
              {field('Animaux de compagnie passés', 'animalsPasses', s3, setS3, { textarea:true, placeholder:'Chien Labrador pendant 12 ans, chat pendant 5 ans…', required: true })}
              {field('Pourquoi souhaitez-vous adopter ?', 'raisonAdoption', s3, setS3, { textarea:true, required: true })}
              {field('Type d\'animal recherché', 'typeAnimal', s3, setS3, { placeholder:'Chien, Chat, Lapin…', required: true })}
              {field('Préférences (âge, taille, sexe, caractère)', 'preferences', s3, setS3, { textarea:true, required: true })}
              {field('Conscient des coûts (nourriture, vétérinaire…) ?', 'conscientCouts', s3, setS3, { placeholder:'Oui, j\'ai prévu un budget mensuel de…', required: true })}
              {field('Face aux problèmes de comportement ?', 'problComportement', s3, setS3, { textarea:true, required: true })}
              {field('En cas d\'absence prolongée ?', 'absenceProlongee', s3, setS3, { textarea:true, placeholder:'Pension vétérinaire, pet-sitter…', required: true })}
            </div>
          </div>
        )}

        {/* ── Section 4 ── */}
        {step === 4 && (
          <div className="af-section">
            <div className="af-section-title">Section 4 — Animaux actuels au foyer</div>
            <div className="af-grid af-grid-1">
              {field('Animaux actuels (espèce, âge, sexe, stérilisé ?)', 'animauxActuels', s4, setS4, { textarea:true, placeholder:'Chat mâle 3 ans, stérilisé', required: true })}
              {field('Leur réaction à d\'autres animaux', 'reactionAnimaux', s4, setS4, { textarea:true, required: true })}
              {field('Vaccinés et identifiés ?', 'vaccinesId', s4, setS4, { placeholder:'Oui, tous vaccinés et identifiés par puce', required: true })}
            </div>
          </div>
        )}

        {/* ── Section 5 ── */}
        {step === 5 && (
          <div className="af-section">
            <div className="af-section-title">Section 5 — Engagements et compréhension</div>
            <div className="af-checks">
              {check("Je comprends que l'adoption est un engagement sur le long terme (10 à 15 ans ou plus)", 'engagementLongTerme')}
              {check("J'accepte les visites de pré-adoption et de post-adoption de l'association", 'accepteVisites')}
              {check("Je suis prêt(e) à offrir à l'animal les soins vétérinaires nécessaires tout au long de sa vie", 'soinVeteriaire')}
              {check("J'accepte de signer un contrat d'adoption avec l'association *", 'accepteContrat')}
            </div>
            <p className="af-required-note">* Champ obligatoire</p>
          </div>
        )}

        {/* ── Section 6 ── */}
        {step === 6 && (
          <div className="af-section">
            <div className="af-section-title">Section 6 — Informations additionnelles</div>
            <div className="af-grid af-grid-1">
              {field('Y a-t-il d\'autres informations à nous communiquer ?', 'autresInfos', s6, setS6, { textarea:true, placeholder:'Toute information utile pour votre dossier…' })}
            </div>
          </div>
        )}

        {/* ── Section 7 — Signature ── */}
        {step === 7 && (
          <div className="af-section">
            <div className="af-section-title">Signature numérique</div>
            <p className="af-sig-info">Signez dans le cadre ci-dessous avec votre souris ou votre doigt (sur mobile). Votre signature sera intégrée au document PDF.</p>

            <div className="af-sig-wrap">
              <canvas
                ref={canvasRef}
                className={`af-canvas${hasSig ? ' has-sig' : ''}`}
                width={600}
                height={180}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={stopDraw}
              />
              {!hasSig && (
                <div className="af-sig-placeholder">
                  <IcoPen />
                  <span>Signez ici</span>
                </div>
              )}
            </div>

            <button className="af-btn-ghost af-clear-sig" onClick={clearSig} disabled={!hasSig}>
              <IcoTrash /> Effacer la signature
            </button>

            <div className="af-sig-recap">
              <div className="af-sig-recap-title">Récapitulatif</div>
              <div className="af-sig-recap-row"><span>Adoptant :</span><strong>{s1.nomPrenom || '—'}</strong></div>
              <div className="af-sig-recap-row"><span>Date :</span><strong>{new Date().toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })}</strong></div>
              <div className="af-sig-recap-row"><span>Contrat accepté :</span><strong style={{ color: s5.accepteContrat ? 'var(--green-dk)' : 'var(--red)' }}>{s5.accepteContrat ? 'Oui ✓' : 'Non ✗'}</strong></div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="af-nav-wrap">
        {!stepValid && step !== 6 && (
          <div className="af-nav-hint">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {stepHint()}
          </div>
        )}
        <div className="af-nav">
          {step > 1 && (
            <button className="af-btn-ghost" onClick={() => { setError(''); setShowErrors(false); setStep(s => s - 1) }}>← Précédent</button>
          )}
          {step < TOTAL_STEPS ? (
            <button
              className="af-btn-primary"
              onClick={() => {
                if (!stepValid) { setShowErrors(true); return }
                setError(''); setShowErrors(false); setStep(s => s + 1)
              }}
              disabled={false}
              title={!stepValid ? stepHint() : ''}
            >
              Suivant →
            </button>
          ) : (
            <button className="af-btn-primary af-btn-submit" onClick={handleSubmit} disabled={saving || !hasSig}>
              {saving ? 'Génération en cours…' : <><IcoSend /> Soumettre et télécharger</>}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}